<script setup>
import { onMounted, watch, computed, defineAsyncComponent } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { initFlowbite } from 'flowbite'
import { useGlobalStore } from '@/stores/GlobalStore'
import { useUserStore } from './stores/UserStore'
import { useListingsStore } from '@/stores/ListingsStore'
import { useConnectionStore } from '@/stores/ConnectionStore'
import { useHomeStore } from '@/stores/HomeStore'
import { useConfigStore } from '@/stores/ConfigStore'
import { useBillingStore } from '@/stores/BillingStore'
import { useKeyboardShortcut } from '@/hooks/useKeyboardShortcut'
import { usePusher } from '@/hooks/usePusher'
import { useCello } from '@/hooks/useCello'
import { useCdpSupport } from '@/hooks/useCdpSupport'
import { useStrapiStore } from '@/stores/StrapiStore'
import MainLayout from '@/components/MainLayout/index.vue'
import GlobalVideoWidget from '@/components/GlobalVideoWidget.vue'
import NewVersionToast from '@/components/NewVersionToast.vue'
import ListingsExportToast from '@/components/ListingsExportToast.vue'
import MaintenancePage from '@/views/MaintenancePage.vue'
import { setErrorHandlerUser } from '@/services/errorHandler'
import ToastNotification from '@/components/ToastNotification.vue'
import UpgradeFeatureModal from '@/components/UpgradePlans/UpgradeFeatureModal.vue'
import UpgradePlanSuccessModal from '@/components/UpgradePlans/UpgradePlanSuccessModal.vue'
import { POSTHOG_EVENTS } from '@/constants/posthog'
import { safeCapture, ensureIdentified } from '@/helpers/posthog'
import BuyScansDialog from '@/views/Settings/components/Plans/BuyScansDialog.vue'

// Async: the router already lazy-imports this component for /upgrade-plans, and a
// static import here cancelled that — vite warned it "will not move module into
// another chunk", so the whole UpgradePlans tree shipped in the entry chunk for
// every user. It renders behind a v-if, so nothing needs it at mount (BD-01).
const UpgradeWindow = defineAsyncComponent(() => import('@/components/UpgradePlans/UpgradeWindow.vue'))

const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE

const route = useRoute()
const router = useRouter()

const userStore = useUserStore()
const globalStore = useGlobalStore()
const listingsStore = useListingsStore()
const configStore = useConfigStore()
const billingStore = useBillingStore()
const connectionStore = useConnectionStore()
const homeStore = useHomeStore()
const strapiStore = useStrapiStore()
const isAuthLayout = computed(() => !route.meta?.requireAuth)
const user = computed(() => userStore.user)

useKeyboardShortcut(router)
usePusher(userStore)
useCello()
useCdpSupport()

globalStore.fetchBuildTxt()
strapiStore.fetchSystemStatusNotificationBar()

setInterval(() => {
  globalStore.fetchBuildTxt()
}, 300000)

onMounted(async () => {
  globalStore.windowWidth = window.innerWidth
  globalStore.windowHeight = window.innerHeight
  window.addEventListener('resize', () => {
    globalStore.windowWidth = window.innerWidth
    globalStore.windowHeight = window.innerHeight

    if (window.innerWidth < 1280) {
      globalStore.sidebar = false
    }

  })

  // initialize components based on data attribute selectors
  initFlowbite()

  const params = new URLSearchParams(window.location.search)
  const authAs = params.get('authAs')
  const sessionOverrideAuthToken = sessionStorage.getItem('cdp_auth_override_token')

  if (authAs || sessionOverrideAuthToken) {
    await userStore.checkSession(authAs || sessionOverrideAuthToken)
    console.log('logged in as', userStore.user?.email)

    if (route.query?.authAs) {
      router.replace('/inventory')
    } else if (route.query?.backTo) {
      router.replace(route.query.backTo)
    }
    
  } else {
    userStore.checkSession()
  }

  if (navigator.userAgent.search('Mac') !== -1) {
    globalStore.userAgent = 'cmd'
  } else {
    globalStore.userAgent = 'ctrl'
  }
  globalStore.sidebar =  JSON.parse(localStorage.getItem('sidebar') || false)
  localStorage.setItem('sidebar', JSON.stringify(globalStore.sidebar))
})

watch(user, () => {
  userStore.user && !configStore.ignoreConfigUpdate && configStore.fetchConfigs({ withoutLoader: true })
  userStore.user && setErrorHandlerUser(userStore.user)
  // Identify before flags matter: `inventory-beta` is targeted per user, so a
  // restored session has to be attached to the right person id.
  userStore.user && ensureIdentified(userStore.user)
  // …then re-assert the beta state the user record already carries (enrollment
  // + opt-in status). `$set` is idempotent and both writes are change-guarded,
  // so this is free — and it is the ONLY thing that repairs an enrollment whose
  // PostHog write was lost (closed tab, dropped request), which would otherwise
  // leave that dealer outside the experiment permanently: the write-once guard
  // on the record never lets it retry.
  userStore.user && userStore.mirrorInventoryBetaState()

  configStore.ignoreConfigUpdate = false

  if (!userStore.flags[`ebay_listings_corrected`]) {
    listingsStore.correctListings('ebay')
  }
})

watch(() => connectionStore.addedChannels.length, (newLen, oldLen) => {
  if (newLen === 1 && oldLen === 0) {
    homeStore.updateCaptureAction('added-connection')

    safeCapture(POSTHOG_EVENTS.CONNECTION_ADDED, {
      userId: userStore.user.id,
      connection_type: connectionStore.addedChannels[0].name,
      timestamp: new Date().toISOString(),
    })
  }
})

/**
 * "ResizeObserver loop completed with undelivered notifications" is NOISE, not a
 * fault.
 *
 * The browser emits it when a resize handler causes another resize inside the
 * same frame; the loop then settles on the next one. Nothing is lost and nothing
 * needs fixing — AG Grid, sticky chrome and the sidebar transition all trigger it
 * routinely. It was being caught and RE-LOGGED through `console.error`, which is
 * what made a benign notification look like a recurring bug in three places.
 *
 * Swallowed here rather than in each observer: it arrives as a window-level
 * error with no stack to attribute, so there is no single observer to fix.
 */
const isResizeObserverNoise = (message) =>
  String(message || '').includes('ResizeObserver loop completed with undelivered notifications')

// Global error handler
window.onerror = function (message, source, lineno, colno, error) {
  // `true` marks it handled, so it never reaches the console at all.
  if (isResizeObserverNoise(message)) return true

  if (error instanceof RangeError) {
    console.error('Global RangeError Caught:', error)
  }

  // Return false to prevent the default handling of the error
  return false
};



// Same noise, arriving as an error EVENT rather than through `window.onerror`.
// Stopped before it propagates instead of logged.
window.addEventListener('error', function (event) {
  if (isResizeObserverNoise(event.message)) {
    event.stopImmediatePropagation()
    event.preventDefault()
  }
});

window.addEventListener('unhandledrejection', function (event) {
  if (isResizeObserverNoise(event.reason?.message)) event.preventDefault()
});
</script>

<template>
  <!-- <RouterView v-if="isAuthLayout || configStore.config?.subscription?.plan_id" /> -->
  <MaintenancePage v-if="isMaintenanceMode === 'true' || globalStore.maintenanceMode" />
  <template v-else>
    <NewVersionToast />
    <ListingsExportToast />
    <ToastNotification />
    <RouterView v-if="isAuthLayout || route.meta?.fullScreen || ['MobileScan', 'MobileScanSides', 'MobileScanUpload', 'MobileScanCompleted'].includes(route.name)" />
    <template v-else>
      <MainLayout />
      <GlobalVideoWidget />

      <UpgradeFeatureModal />
      <UpgradeWindow v-if="!configStore.isComplimentary && billingStore.upgradeWindow" @close="billingStore.upgradeWindow = false" />
      <UpgradePlanSuccessModal v-if="billingStore.upgradePlanSuccessModal" @close="billingStore.upgradePlanSuccessModal = false" />
      <BuyScansDialog />
    </template>
  </template>
</template>